import { useCallback } from 'react';

import {
  useAssembleHoF,
  usePublishHoF,
  useUnpublishHoF,
  useRemoveHoF,
} from 'actions/hall-of-fame';

import { recordName } from '../queue-fields';

const countOf = (data, key) => ((data && data[key]) || []).length;

// Everything on this screen that writes: running the assemble agent, and the three
// decisions on a record. The two irreversible ones confirm first, naming what becomes
// public.
const useRecordActions = ({
  toast,
  confirmThen,
  refresh,
  reportOutcome,
  closeEditor,
}) => {
  const afterDecision = useCallback(
    verb =>
      reportOutcome(verb, () => {
        closeEditor();
        refresh();
      }),
    [reportOutcome, closeEditor, refresh],
  );

  const { post: assemble, isProcessing: assembling } = useAssembleHoF({
    onComplete: (error, data) => {
      if (error) {
        toast(`Agent failed: ${error}`);
        return;
      }
      toast(
        `Agent finished: ${countOf(data, 'ready')} ready, ${countOf(
          data,
          'parked',
        )} parked`,
      );
      refresh();
    },
  });

  const { post: publish } = usePublishHoF({ onComplete: afterDecision('Publish') });
  const { post: unpublish } = useUnpublishHoF({ onComplete: afterDecision('Unpublish') });
  const { post: remove } = useRemoveHoF({ onComplete: afterDecision('Remove') });

  // All three are handed to memoized queue rows, so they are stable across renders.
  const askPublish = useCallback(
    row =>
      confirmThen(
        `Publish ${recordName(row)} to the public Hall of Fame?`,
        "This makes the student's name, grade, state and photo publicly visible on cuemath.com.",
        () => publish({}, row.id),
      ),
    [confirmThen, publish],
  );

  const askUnpublish = useCallback(
    row =>
      confirmThen(
        `Unpublish ${recordName(row)}?`,
        'The card is withdrawn from the public site immediately and returns to draft.',
        () => unpublish({}, row.id),
      ),
    [confirmThen, unpublish],
  );

  const askRemove = useCallback(
    row =>
      confirmThen(
        `Remove ${recordName(row)}?`,
        'The record is withdrawn from the public feed. There is no restore action on this screen.',
        () => remove({}, row.id),
      ),
    [confirmThen, remove],
  );

  return {
    runAgent: useCallback(() => assemble({}), [assemble]),
    assembling,
    askPublish,
    askUnpublish,
    askRemove,
  };
};

export default useRecordActions;
